import { useContext, useState, useEffect } from 'react'; // 🚀 Added useEffect

import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Snackbar,
  Alert,
  Badge // 🚀 Added Badge for support ticket counts
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import {
  AssessmentOutlined,
  DashboardOutlined,
  DownloadOutlined,
  HelpOutline,
  ListAltOutlined,
  LockOutlined,
  MailOutline,
  PersonOutline,
  StackedLineChartOutlined,
  SupportAgentOutlined,
  SyncAltOutlined,
  UploadFileOutlined
} from '@mui/icons-material';
import { useCurrentUser } from 'src/store/auth.store';
import { useSupportStore } from 'src/store/support.store';
import ContactDialog from 'src/content/pages/Login/ContactDialog';

const MenuWrapper = styled(Box)(
  ({ theme }) => `
  .MuiList-root {
    padding: ${theme.spacing(1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, 2.5)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(Box)(
  ({ theme }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${
              theme.colors.error.main
            }; /* 🚀 Customized badge styling context */
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.error.contrastText};
          }
        }
    
        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: flex-start;
          padding: ${theme.spacing(1.2, 3)};

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[30]};
            font-size: ${theme.typography.pxToRem(20)};
            margin-right: ${theme.spacing(1)};
          }
          
          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(20)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.06)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

function SidebarMenu() {
  const { closeSidebar } = useContext(SidebarContext);
  const user = useCurrentUser();

  const [contactOpen, setContactOpen] = useState(false);
  const [contactSuccessOpen, setContactSuccessOpen] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  // 🚀 Admin support dashboard display toggle state
  const [adminSupportOpen, setAdminSupportOpen] = useState(false);

  // Connect store properties
  const {
    messages,
    fetchAllContactMessages,
    submitContactForm,
    loading: contactLoading
  } = useSupportStore();

  // 🚀 Quietly populate ticket tracking records on layout mount to drive live status badge metrics
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAllContactMessages().catch(() => {});
    }
  }, [fetchAllContactMessages, user?.role]);

  const handleContactSubmit = async (data: {
    name: string;
    email: string;
    message: string;
  }) => {
    setContactError(null);
    try {
      await submitContactForm(data);
      setContactOpen(false);
      setContactSuccessOpen(true);
    } catch (err) {
      setContactError(
        typeof err === 'string'
          ? err
          : 'Failed to submit contact message. Please try again.'
      );
    }
  };

  const handleOpenSupportDesk = async () => {
    closeSidebar();
    setAdminSupportOpen(true);
    try {
      await fetchAllContactMessages();
    } catch (err) {
      // Error is caught safely by standard store handlers
    }
  };

  // 🚀 Dynamically derive total unresolved records to flag badge updates
  const pendingCount = messages.filter((m) => m.status === 'PENDING').length;

  return (
    <>
      <MenuWrapper>
        {/* OVERVIEW */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Overview
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/dashboards/insights"
                  startIcon={<DashboardOutlined />}
                >
                  Novex Insights
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        {/* PORTFOLIO */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Portfolio
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/holdings"
                  startIcon={<StackedLineChartOutlined />}
                >
                  Holdings
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/transactions"
                  startIcon={<ListAltOutlined />}
                >
                  Transactions
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/csv-import"
                  startIcon={<UploadFileOutlined />}
                >
                  Import CSV
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/corporate-actions"
                  startIcon={<SyncAltOutlined />}
                >
                  Corporate Actions
                </Button>
              </ListItem>
            </List>
          </SubMenuWrapper>
        </List>

        {/* REPORTS */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Reports
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/tax-reports"
                  startIcon={<AssessmentOutlined />}
                >
                  Tax Reports
                </Button>
              </ListItem>
              {user?.role === 'admin' && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/profile/tax-reports"
                    startIcon={<DownloadOutlined />}
                  >
                    Export Data
                  </Button>
                </ListItem>
              )}
            </List>
          </SubMenuWrapper>
        </List>

        {/* ACCOUNTS */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Accounts
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/details"
                  startIcon={<PersonOutline />}
                >
                  Profile
                </Button>
              </ListItem>
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/change-password"
                  startIcon={<LockOutlined />}
                >
                  Change password
                </Button>
              </ListItem>
              {user?.role === 'admin' && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/profile/user-directory"
                    startIcon={<PersonOutline />}
                  >
                    User Directory
                  </Button>
                </ListItem>
              )}
            </List>
          </SubMenuWrapper>
        </List>

        {/* HELP */}
        <List
          component="div"
          subheader={
            <ListSubheader component="div" disableSticky>
              Help
            </ListSubheader>
          }
        >
          <SubMenuWrapper>
            <List component="div">
              <ListItem component="div">
                <Button
                  disableRipple
                  component={RouterLink}
                  onClick={closeSidebar}
                  to="/management/profile/details"
                  startIcon={<HelpOutline />}
                >
                  FAQ
                </Button>
              </ListItem>

              <ListItem component="div">
                <Button
                  disableRipple
                  startIcon={<MailOutline />}
                  onClick={() => {
                    closeSidebar();
                    setContactError(null);
                    setContactOpen(true);
                  }}
                >
                  Contact
                </Button>
              </ListItem>

              {user?.role === 'admin' && (
                <ListItem component="div">
                  <Button
                    disableRipple
                    component={RouterLink}
                    onClick={closeSidebar}
                    to="/management/support" // 🚀 Point this to your new router layout path
                    startIcon={<SupportAgentOutlined />}
                    endIcon={
                      pendingCount > 0 ? (
                        <Badge
                          badgeContent={pendingCount}
                          color="error"
                          max={99}
                        />
                      ) : undefined
                    }
                  >
                    Support
                  </Button>
                </ListItem>
              )}
            </List>
          </SubMenuWrapper>
        </List>
      </MenuWrapper>

      <ContactDialog
        open={contactOpen}
        onClose={() => {
          setContactOpen(false);
          setContactError(null);
        }}
        onSubmitContact={handleContactSubmit}
        loading={contactLoading}
        error={contactError}
      />

      <Snackbar
        open={contactSuccessOpen}
        autoHideDuration={4000}
        onClose={() => setContactSuccessOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setContactSuccessOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 500 }}
        >
          Message sent successfully! <br />
          Our support team will get back to you shortly.
        </Alert>
      </Snackbar>
    </>
  );
}

export default SidebarMenu;
